import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, Crown, Award, Star, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  platinum: {
    label: 'Platinum Partners',
    subtitle: 'Our most valued partners powering everything we do',
    icon: Crown,
    accent: 'from-amber-200 via-amber-100 to-yellow-50',
    border: 'border-amber-300',
    ring: 'ring-amber-400/40',
    chip: 'bg-amber-500 text-white',
    cardSize: 'aspect-[16/9]',
    cols: 'grid-cols-1 md:grid-cols-2',
    order: 0,
  },
  gold: {
    label: 'Gold Partners',
    subtitle: 'Championship-level supporters',
    icon: Award,
    accent: 'from-yellow-100 via-amber-50 to-orange-50',
    border: 'border-yellow-400',
    ring: 'ring-yellow-400/30',
    chip: 'bg-yellow-500 text-white',
    cardSize: 'aspect-[4/3]',
    cols: 'grid-cols-2 md:grid-cols-3',
    order: 1,
  },
  silver: {
    label: 'Silver Partners',
    subtitle: 'Trusted brands behind every run',
    icon: Star,
    accent: 'from-slate-100 via-gray-50 to-white',
    border: 'border-slate-300',
    ring: 'ring-slate-400/30',
    chip: 'bg-slate-500 text-white',
    cardSize: 'aspect-[4/3]',
    cols: 'grid-cols-2 md:grid-cols-4',
    order: 2,
  },
  supporter: {
    label: 'Community Supporters',
    subtitle: 'Friends of the program',
    icon: Heart,
    accent: 'from-rose-50 via-pink-50 to-white',
    border: 'border-rose-200',
    ring: 'ring-rose-200/30',
    chip: 'bg-rose-500 text-white',
    cardSize: 'aspect-square',
    cols: 'grid-cols-3 md:grid-cols-6',
    order: 3,
  },
};

const SponsorsPage = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/sponsors`)
      .then(res => setSponsors(res.data.sponsors || []))
      .catch(() => setSponsors([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by tier and respect order
  const grouped = sponsors.reduce((acc, s) => {
    const tier = s.tier || 'supporter';
    if (!acc[tier]) acc[tier] = [];
    acc[tier].push(s);
    return acc;
  }, {});

  // Tier order
  const orderedTiers = Object.keys(grouped).sort(
    (a, b) => (TIER_CONFIG[a]?.order ?? 99) - (TIER_CONFIG[b]?.order ?? 99)
  );

  // Marquee for hero — all sponsors in one row
  const allLogos = sponsors.filter(s => s.logo_url);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-primary/70 text-primary-foreground">
        <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 50%)'
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="font-display font-bold uppercase tracking-widest text-sm mb-3 opacity-80">Powered By Partners</p>
          <h1 className="font-display font-black text-5xl md:text-7xl uppercase tracking-tight leading-none">
            Our<br /><span className="text-yellow-300">Sponsors</span>
          </h1>
          <p className="font-serif text-lg md:text-xl mt-6 max-w-2xl mx-auto opacity-90">
            The brands and businesses that make Elevated Roping Media possible. Their support powers every clinic, jackpot, and broadcast.
          </p>
        </div>

        {/* Scrolling marquee of all logos */}
        {allLogos.length > 0 && (
          <SponsorMarquee logos={allLogos} />
        )}
      </section>

      {/* Tiered grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        )}

        {!loading && sponsors.length === 0 && (
          <div className="text-center py-16">
            <Award className="w-16 h-16 mx-auto opacity-30 mb-4" />
            <h2 className="font-display font-bold text-2xl uppercase mb-2">Be Our First Partner</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              We're building a roster of trusted brands. Join us and reach our growing community of ropers.
            </p>
            <Link to="/sponsorship">
              <Button size="lg" className="rounded-full">View Partnership Packages</Button>
            </Link>
          </div>
        )}

        {orderedTiers.map(tier => {
          const cfg = TIER_CONFIG[tier] || TIER_CONFIG.supporter;
          const TierIcon = cfg.icon;
          const items = grouped[tier];
          return (
            <section key={tier} data-testid={`sponsor-tier-${tier}`}>
              <div className="flex items-start gap-3 mb-6 border-b-2 border-border pb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${cfg.chip} flex-shrink-0`}>
                  <TierIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display font-black text-2xl md:text-4xl uppercase tracking-tight">
                    {cfg.label}
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground mt-1">{cfg.subtitle}</p>
                </div>
                <span className="text-xs text-muted-foreground hidden sm:block">
                  {items.length} {items.length === 1 ? 'partner' : 'partners'}
                </span>
              </div>

              <div className={`grid gap-4 sm:gap-6 ${cfg.cols}`}>
                {items.map(sponsor => (
                  <SponsorCard key={sponsor.id} sponsor={sponsor} cfg={cfg} />
                ))}
              </div>
            </section>
          );
        })}

        {/* CTA */}
        <section className="border-2 border-primary/30 rounded-3xl bg-gradient-to-br from-primary/10 to-primary/5 p-8 md:p-12 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight">
            Become A Partner
          </h2>
          <p className="text-muted-foreground mt-3 text-lg max-w-2xl mx-auto">
            Get your brand in front of an engaged community of competitive ropers, coaches, and ranch hands.
          </p>
          <Link to="/sponsorship">
            <Button size="lg" className="rounded-full px-10 mt-6 font-bold uppercase tracking-wider" data-testid="sponsorship-packages-cta">
              View Partnership Packages
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </section>
      </div>
    </div>
  );
};

const SponsorCard = ({ sponsor, cfg }) => {
  const inner = (
    <div className={`group h-full bg-white rounded-2xl border-2 ${cfg.border} hover:ring-4 hover:${cfg.ring} hover:scale-[1.02] transition-all shadow-md hover:shadow-xl overflow-hidden`}>
      <div className={`${cfg.cardSize} bg-gradient-to-br ${cfg.accent} flex items-center justify-center p-6`}>
        {sponsor.logo_url ? (
          <img
            src={sponsor.logo_url}
            alt={sponsor.name}
            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
            draggable={false}
          />
        ) : (
          <span className="font-display font-black text-2xl text-foreground/30 uppercase">{sponsor.name}</span>
        )}
      </div>
      <div className="p-4 bg-background">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg uppercase tracking-tight truncate">{sponsor.name}</h3>
            {sponsor.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{sponsor.description}</p>
            )}
          </div>
          {sponsor.website && (
            <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0 mt-1" />
          )}
        </div>
      </div>
    </div>
  );

  if (sponsor.website) {
    return (
      <a
        href={sponsor.website}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`sponsor-card-${sponsor.id}`}
      >
        {inner}
      </a>
    );
  }
  return <div data-testid={`sponsor-card-${sponsor.id}`}>{inner}</div>;
};

// Continuous horizontal marquee of all logos
const SponsorMarquee = ({ logos }) => {
  const trackRef = useRef(null);
  // Duplicate logos for seamless infinite scroll
  const doubled = [...logos, ...logos];

  return (
    <div className="relative bg-black/10 backdrop-blur py-6 overflow-hidden">
      <div
        ref={trackRef}
        className="flex gap-12 animate-marquee whitespace-nowrap"
        style={{ animation: 'marquee 40s linear infinite' }}
      >
        {doubled.map((s, i) => (
          <a
            key={`${s.id}-${i}`}
            href={s.website || '#'}
            target={s.website ? '_blank' : undefined}
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center justify-center h-16 sm:h-20 px-4 hover:scale-110 transition-transform"
            title={s.name}
          >
            {s.logo_url ? (
              <img src={s.logo_url} alt={s.name} className="h-full w-auto object-contain" draggable={false} />
            ) : (
              <span className="text-white font-display font-bold text-xl uppercase">{s.name}</span>
            )}
          </a>
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default SponsorsPage;
